'use client';

import dynamic from 'next/dynamic';
import { Card, CardContent } from '@/app/_components/core/card';
import { Copytext } from '@/app/_components/core/copytext';
import { Headline } from '@/app/_components/core/headline';
import { RichText } from '@/app/_components/core/rich-text';
import { cn } from '@/app/_lib/utils';
import type { FormEmbedComponentProps, EventMeta } from './form-embed.types';

// Custom form handler - loaded from @custom/_exports registry
// Allows project-specific form providers (HubSpot, etc.)
const FormEmbedFormHandler = dynamic(
  () =>
    import('@custom/_exports').then((mod) => ({
      default: mod.FormEmbedFormHandler,
    })),
  { ssr: false }
);

type FormEmbed1ColProps = Pick<FormEmbedComponentProps, 'component'> & {
  eventMeta?: EventMeta;
};

export function FormEmbed1Col({ component, eventMeta }: FormEmbed1ColProps) {
  const { embedIntro, html, theme } = component;
  const hasShadow = theme === 'theme0';

  return (
    <div className="container">
      <Card
        variant="elevated"
        className={cn('w-full py-10 lg:py-20', hasShadow ? '' : 'shadow-none')}
      >
        <CardContent className="px-5 lg:px-10">
          <div className="mx-auto grid w-full max-w-lg justify-items-center">
            {embedIntro && (
              <RichText className="mb-8 w-full max-w-lg text-center *:w-fit" classConfig={{ list: 'mx-auto' }}>
                {embedIntro}
              </RichText>
            )}
            {eventMeta && (
              <div className="mb-8 w-full text-center">
                <Headline as="h4">{eventMeta.title}</Headline>
                <Copytext className="mb-0">
                  <strong>{eventMeta.dateTimeLabel}:</strong> {eventMeta.dateTime}
                  {eventMeta.location && (
                    <>
                      <br />
                      <strong>{eventMeta.locationLabel}:</strong> {eventMeta.location}
                    </>
                  )}
                </Copytext>
              </div>
            )}
            <FormEmbedFormHandler html={html} variant="1col" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
